import { G } from '@mobily/ts-belt';
import { addHours, addMonths, differenceInDays } from 'date-fns';
import { Decimal } from 'decimal.js';
import { match } from 'ts-pattern';

import { B2bCampaignActionHandler } from '@Common/types/bo/b2b/contracts';
import { IntegerAmount } from '@Common/types/currency/currency';

export function computeDaysAfterFiscalDeadline({
  endDate,
  fiscalPeriodStart,
  fiscalPeriodEnd,
  campaignType,
  choiceCollectedBy,
}: {
  endDate: Date;
  fiscalPeriodStart: Date;
  fiscalPeriodEnd: Date;
  campaignType: string;
  choiceCollectedBy: B2bCampaignActionHandler | null;
}): number {
  const endDateStartOfDay = new Date(
    Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), endDate.getUTCDate()),
  );

  const fundsRequestDate =
    choiceCollectedBy === 'distributor' ? addHours(endDateStartOfDay, 36) : endDateStartOfDay;

  return match(campaignType)
    .with('participation', () => {
      const participationDeadlineMonth = addMonths(fiscalPeriodEnd, 6);
      // Deadline = first day of the month
      const participationDeadline = new Date(
        Date.UTC(
          participationDeadlineMonth.getUTCFullYear(),
          participationDeadlineMonth.getUTCMonth(),
          1,
        ),
      );

      if (fundsRequestDate >= participationDeadline) {
        return differenceInDays(fundsRequestDate, participationDeadline);
      }
      return 0;
    })
    .with('profit_sharing', () => {
      const fiscalPeriodDays = differenceInDays(fiscalPeriodEnd, fiscalPeriodStart);
      const profitSharingDeadlineMonth =
        fiscalPeriodDays >= 364 ? addMonths(fiscalPeriodEnd, 5) : addMonths(fiscalPeriodEnd, 3);
      // Deadline = last day of the month
      const profitSharingDeadline = new Date(
        Date.UTC(
          profitSharingDeadlineMonth.getUTCFullYear(),
          profitSharingDeadlineMonth.getUTCMonth() + 1,
          0, // last day of the previous month
        ),
      );
      if (fundsRequestDate >= profitSharingDeadline) {
        return differenceInDays(fundsRequestDate, profitSharingDeadline);
      }
      return 0;
    })
    .otherwise(() => 0);
}

export function computeLateInterestsAmount({
  amount,
  daysAfterFiscalDeadline,
  tmoPct,
}: {
  amount: IntegerAmount<2>;
  daysAfterFiscalDeadline: number;
  tmoPct: number;
}): number {
  return new Decimal(amount)
    .mul(daysAfterFiscalDeadline)
    .mul(tmoPct * 1.33)
    .div(100)
    .div(365)
    .round()
    .toNumber();
}

export function resolveLateInterestsAmount({
  amount,
  daysAfterFiscalDeadline,
  tmoPct,
  isAutomatic,
  overrideAmount,
}: {
  amount: IntegerAmount<2>;
  daysAfterFiscalDeadline: number;
  tmoPct: number | null;
  isAutomatic: boolean;
  overrideAmount: number | null | undefined;
}): number {
  if (isAutomatic) {
    if (G.isNullable(tmoPct)) {
      return 0;
    }
    return computeLateInterestsAmount({
      amount,
      daysAfterFiscalDeadline,
      tmoPct,
    });
  }

  if (G.isNotNullable(overrideAmount) && overrideAmount > 0) {
    return overrideAmount;
  }

  return 0;
}

export function calculateProRataLateInterest({
  allocationGross,
  totalAllocationsGross,
  totalLateInterest,
}: {
  allocationGross: IntegerAmount<2>;
  totalAllocationsGross: number;
  totalLateInterest: number;
}): number {
  return new Decimal(totalLateInterest)
    .mul(allocationGross)
    .div(totalAllocationsGross)
    .round()
    .toNumber();
}
